<template>
  <footer class="app-footer">
    <div class="footer-container">
      <!-- 关于部分 -->
      <div class="footer-section">
        <h3>关于 VizLab</h3>
        <p class="footer-description">
          VizLab 是一个交互式科学概念可视化平台，通过参数调节和实时可视化帮助用户理解基础科学原理。
        </p>
        <div class="footer-logo">
          <img src="/logo.png" alt="VizLab Logo" class="logo-icon" />
          <span class="logo-text">VizLab</span>
        </div>
      </div>

      <!-- 快速链接 -->
      <div class="footer-section">
        <h3>快速链接</h3>
        <ul class="footer-links">
          <li>
            <router-link to="/">首页</router-link>
          </li>
          <li>
            <router-link to="/search">搜索</router-link>
          </li>
          <li v-for="category in categories.slice(0, 4)" :key="category.id">
            <router-link :to="`/?category=${category.id}`">
              {{ category.icon }} {{ category.name }}
            </router-link>
          </li>
        </ul>
      </div>

      <!-- 学科分类 -->
      <div class="footer-section">
        <h3>学科分类</h3>
        <ul class="footer-links">
          <li v-for="category in categories" :key="category.id">
            <a href="#" @click.prevent="filterByCategory(category.id)">
              {{ category.icon }} {{ category.name }}
            </a>
          </li>
        </ul>
      </div>

      <!-- 联系方式 -->
      <div class="footer-section">
        <h3>联系我们</h3>
        <ul class="footer-contact">
          <li>
            <span class="contact-icon">📧</span>
            <a href="mailto:lele.shu@gmail.com">lele.shu@gmail.com</a>
          </li>
          <li>
            <span class="contact-icon">💬</span>
            <a href="#" @click.prevent="goFeedback">反馈</a>
          </li>
        </ul>
      </div>
    </div>

    <!-- 版权信息 -->
    <div class="footer-bottom">
      <div class="footer-container">
        <p class="copyright">
          © {{ currentYear }} VizLab. All rights reserved.
        </p>
        <div class="footer-meta">
          <a href="http://SHUD.xyz" target="_blank" rel="noopener">SHUD.xyz</a>
          <span class="separator">|</span>
          <a href="#" @click.prevent="showPrivacy">隐私政策</a>
          <span class="separator">|</span>
          <a href="#" @click.prevent="showTerms">使用条款</a>
          <span class="separator">|</span>
          <span class="version">v1.0.0</span>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import categoriesData from '@/data/categories.json'

const router = useRouter()

// 分类数据
const categories = categoriesData.categories

// 当前年份
const currentYear = computed(() => new Date().getFullYear())

/**
 * 按分类过滤
 */
function filterByCategory(categoryId) {
  router.push({ path: '/', query: { category: categoryId } })
}

/**
 * 跳转到反馈页面
 */
function goFeedback() {
  router.push('/feedback')
}

/**
 * 显示隐私政策
 */
function showPrivacy() {
  alert('隐私政策页面即将推出！')
}

/**
 * 显示使用条款
 */
function showTerms() {
  alert('使用条款页面即将推出！')
}
</script>

<style scoped>
.app-footer {
  background-color: #2c3e50;
  color: #ecf0f1;
  margin-top: auto;
}

.footer-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem 2rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
}

/* Footer 部分 */
.footer-section h3 {
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  color: #fff;
  font-weight: 600;
}

.footer-description {
  margin: 0 0 1.5rem 0;
  line-height: 1.6;
  color: #bdc3c7;
  font-size: 0.9rem;
}

.footer-logo {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.2rem;
  font-weight: 600;
  color: #3498db;
}

.logo-icon {
  width: 1.5rem;
  height: 1.5rem;
  object-fit: contain;
  display: block;
}

/* 链接列表 */
.footer-links,
.footer-contact {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer-links li,
.footer-contact li {
  margin-bottom: 0.75rem;
}

.footer-links a,
.footer-contact a {
  color: #bdc3c7;
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.2s;
  display: inline-block;
}

.footer-links a:hover,
.footer-contact a:hover {
  color: #3498db;
}

/* 联系方式 */
.footer-contact li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.contact-icon {
  font-size: 1.2rem;
}

/* 底部版权 */
.footer-bottom {
  border-top: 1px solid #34495e;
  background-color: #1a252f;
}

.footer-bottom .footer-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  grid-template-columns: 1fr;
}

.copyright {
  margin: 0;
  font-size: 0.9rem;
  color: #95a5a6;
}

.footer-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.85rem;
}

.footer-meta a {
  color: #95a5a6;
  text-decoration: none;
  transition: color 0.2s;
}

.footer-meta a:hover {
  color: #3498db;
}

.separator {
  color: #7f8c8d;
}

.version {
  color: #7f8c8d;
  font-family: monospace;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .footer-container {
    grid-template-columns: 1fr;
    padding: 2rem 1rem;
  }

  .footer-bottom .footer-container {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
    padding: 1.5rem 1rem;
  }

  .footer-meta {
    flex-wrap: wrap;
    justify-content: center;
  }
}
</style>
